/**
 * Pages that never use the visitor's sign-in session, even when they have one.
 *
 * - /feedback (27 Oct 2026): the anonymous outreach feedback page. An answer must never be tied to
 *   a visit, and a signed-in visit leaves traces the page itself cannot prevent: the launch
 *   refresh (/auth/me, which writes a UserApiInteractions row), the live messaging connection, and
 *   the signed-in widgets that each make their own requests (messages, the assistant, the prompt
 *   dock). OUTREACH-V1-TDD-PLAN.md checkpoint 11 fix round 1.
 *
 * The ONE rule: the auth plugin (whether init uses the session) and app.vue (whether the signed-in
 * widgets mount) both ask it, so the two cannot disagree. Pure, imported explicitly.
 */
export function sessionFreeRoute(path) {
    if (typeof path !== 'string') return false;
    return path === '/feedback' || path.startsWith('/feedback/');
}

/**
 * What init does with the session. With `session` false (a session free page) nothing runs: no
 * sign in header is set, no live connection starts, the cookies are not re-issued, no /auth/me
 * refresh is made, and a 401 does not sign anyone out (no request on that page carries a sign in,
 * so a 401 there says nothing about the session). Every other page: exactly as before.
 */
export function sessionSteps({session = true, authenticated = false} = {}) {
    const on = session !== false;
    return {
        authHeaders: on,
        liveConnection: on,
        touchCookies: on && authenticated === true,
        refresh: on && authenticated === true,
        logoutOn401: on,
    };
}

/**
 * The first move off a session free page starts the session, once (27 Oct 2026, checkpoint 11
 * fix round 2). A signed-in visitor who lands on /feedback and then moves within the app (the 404
 * page's "Go home", a link, the back button) would otherwise reach an ordinary page with no sign
 * in header, no live connection, no logout on 401 and no /auth/me refresh, because the load
 * skipped them on purpose. `start` is init with the session on, exactly as a normal page load.
 *
 * Returns the handler for each move (called with the destination path). It starts nothing when
 * the load was an ordinary page (the load already started the session), nothing for a move that
 * stays on a session free page, and nothing after the first start. A failed start never blocks
 * the move: init already treats an unreachable API as no change.
 */
export function sessionStarterAfter(initialPath, start) {
    let waiting = sessionFreeRoute(initialPath);
    return async (path) => {
        if (!waiting || sessionFreeRoute(path)) return false;
        waiting = false;
        try {
            await start();
        } catch (e) {
            // The move goes ahead; the next page load starts the session again.
        }
        return true;
    };
}

/**
 * The path of the page being rendered (27 Oct 2026, checkpoint 11 fix round 3). On the server it
 * is not always the request's own path: a server error (a 404 for /feedback with no token, say) is
 * rendered a SECOND time by Nuxt at /__nuxt_error?url=..., with the visitor's cookies forwarded.
 * Asked with the request path, that second render read as an ordinary page and refreshed the
 * session (/auth/me, a UserApiInteractions row). `ssrUrl` is the server render's own url
 * (nuxtApp.ssrContext.url, set to the failing page on the error render); the /__nuxt_error url
 * query is read as well in case it is missing. In the browser, pass the location and nothing else.
 */
export function pageBeingRendered(requestUrl, ssrUrl) {
    if (typeof ssrUrl === 'string' && ssrUrl.startsWith('/')) return ssrUrl.split(/[?#]/)[0];
    const path = requestUrl?.pathname ?? '';
    if (path === '/__nuxt_error') {
        const failing = requestUrl.searchParams?.get('url');
        if (typeof failing === 'string' && failing.startsWith('/')) return failing.split(/[?#]/)[0];
    }
    return path;
}
